import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ICvxClaimsPrincipal } from "@cvx/cal";
import { CaseInsensitiveComparer } from "src/app/helpers/caseInsensitiveComparer";
import { IGenericApiResponse } from "src/app/models/common/GenericApiResponse";
import { IApprovalReviews } from "src/app/models/components/IApprovalReviews";
import { WorkflowRequestTypes } from "src/app/models/requests/enums/WorkflowRequestTypes.enum";
import { WorkflowStates } from "src/app/models/requests/enums/WorkflowStates.enum";
import { IWorkflowRequest, IWorkflowReview } from "src/app/models/requests/WorkflowRequest";
import { WorkflowRequestService } from "src/app/services/workflow/WorkflowRequestService";

@Component({
    selector: 'request-details-approvals',
    templateUrl: './request-details-approvals.component.html'
})

export class RequestDetailsApprovals implements OnInit {

    @Input() workflowRequest: IWorkflowRequest;
    @Input() principal: ICvxClaimsPrincipal;
    @Output() doneLoading = new EventEmitter<boolean>();

    get requestTypes() { return WorkflowRequestTypes; }
    /**
     * page rendering controls
     */
    isSubmitted: boolean = false;
    approvalForm = this.formBuilder.group({
        decision: [true, Validators.required],
        issueNumber: [''], //Removed initial validators
        comments: ['']
    });
    reviewerCols = ['approvedBy', 'reviewDate', 'approved', 'comments'];

    /**
     * loaded data
     */
    reviews: IApprovalReviews[] = [];
    currentUserIsApprover: boolean = false;

    get WorkflowStates() { return WorkflowStates; }
    hasEmittedDoneLoading: boolean = false;

    constructor(private router: Router, private workflowRequestService: WorkflowRequestService,
        private formBuilder: FormBuilder) { }

    ngOnInit(): void {
        if (this.workflowRequest.type === WorkflowRequestTypes.ServiceAccountDeferment) {
            this.reviewerCols = ['approvedBy', 'reviewDate', 'approved', 'comments', 'issueNumber'];

            //set up conditional validation for issue number based on decision
            this.setupIssueNumberValidation();

            // Listen to decision changes to update issue number validation
            this.approvalForm.get('decision')?.valueChanges.subscribe(decision => {
                this.updateIssueNumberValidation(decision ?? true);
            });
        }

        else {
            // clear issue number validators if not a service account deferment request
            const valueControl = this.approvalForm.get('issueNumber');
            valueControl?.clearValidators();
        }

        for (let r of this.workflowRequest.reviews) {
            this.addReviews(r);
        }

        if (
            this.workflowRequest.state === WorkflowStates.PendingApproval &&
            this.workflowRequest.authorizedApprovers.some(aa => CaseInsensitiveComparer(aa.id, this.principal.objectId)) &&
            !this.workflowRequest.reviews.some(review => CaseInsensitiveComparer(review.reviewer?.id, this.principal.objectId))
        ) {
            this.currentUserIsApprover = true;
        }

        this.isDoneLoading();
    }

    addReviews(review: IWorkflowReview): void {
        this.reviews.push({
            approvedBy: review.reviewer?.email ?? '',
            reviewDate: review.reviewedDate?.toLocaleString('en-us') ?? '',
            approved: review.approved,
            comments: review.comment ?? '',
            issueNumber: review.issueNumber
        });
    }

    isDoneLoading() {
        this.doneLoading.emit(true);
        this.hasEmittedDoneLoading = true;
    }

    onSubmit() {
        if (this.approvalForm.valid) {
            this.isSubmitted = true;
            this.workflowRequestService.ReviewRequest(
                this.workflowRequest.type as WorkflowRequestTypes,
                this.workflowRequest.id,
                this.approvalForm.controls.decision.value ?? false,
                this.approvalForm.controls.comments.value ?? '',
                this.approvalForm.controls.issueNumber.value ?? undefined
            ).subscribe({
                next: (x: IGenericApiResponse<IWorkflowReview>) => {
                    this.addReviews(x.data);
                    this.resetAndReload();
                },
                error: (err: any) => {
                    this.isSubmitted = false;
                },
                complete: () => {
                    this.isSubmitted = false;
                }
            });
        }
    }

    private resetAndReload() {
        this.router.navigated = false;
        this.router.navigate([this.router.url]);
    }

    private setupIssueNumberValidation(): void {
        const decision = this.approvalForm.get('decision')?.value ?? true
        this.updateIssueNumberValidation(decision);
    }

    private updateIssueNumberValidation(decision: boolean): void {
        const issueNumberControl = this.approvalForm.get('issueNumber');

        if (decision === true) {
            // Approved - issue number is required
            issueNumberControl?.setValidators([Validators.required, Validators.pattern('^[a-zA-Z]+[0-9]+$')]);
        } else {
            // Denied - issue number is not required
            issueNumberControl?.clearValidators();
            issueNumberControl?.setValue(''); // Clear the value when denied
        }

        issueNumberControl?.updateValueAndValidity();
    }
    // Helper method to check if issue number is required
    isIssueNumberRequired(): boolean {
        return this.workflowRequest.type === WorkflowRequestTypes.ServiceAccountDeferment &&
            this.approvalForm.get('decision')?.value === true;  // Only required when approved (true)
    }
}